import { isNativeShell } from "@/lib/platform";

/*
 * Where the API lives.
 *
 * A browser tab is served by the same nginx that proxies /api, so a relative base works.
 * The Android and Windows apps are not served by the server at all: a director types the
 * server address once at sign-in and the device remembers it. VITE_API_BASE overrides both
 * (used by the dev server and by builds pinned to one server).
 */

const SERVER_KEY = "pbo.server";
const BROWSER_API_BASE = "/api";

/** "https://app.example.com/" becomes "https://app.example.com"; anything else is refused. */
export function normaliseServerUrl(value: string): string {
  const trimmed = value.trim().replace(/\/+$/, "");
  if (!/^https?:\/\/[^\s/]+$/i.test(trimmed)) {
    throw new Error("Enter the server address, for example https://invoices.example.com");
  }
  return trimmed;
}

export function getServerUrl(): string | null {
  try {
    return localStorage.getItem(SERVER_KEY);
  } catch {
    return null;
  }
}

export function setServerUrl(value: string): string {
  const url = normaliseServerUrl(value);
  try {
    localStorage.setItem(SERVER_KEY, url);
  } catch {
    /* storage unavailable: the address lasts for this run only */
  }
  return url;
}

export function clearServerUrl(): void {
  try {
    localStorage.removeItem(SERVER_KEY);
  } catch {
    /* storage unavailable */
  }
}

/**
 * True when it is the director who says where the server is: the apps, unless the build was
 * pinned to one server. A pinned build must not ask, because the answer would be ignored.
 */
export function asksForServerUrl(): boolean {
  return isNativeShell() && !import.meta.env.VITE_API_BASE;
}

/** True when the app cannot reach any server until an address is entered. */
export function needsServerUrl(): boolean {
  return asksForServerUrl() && getServerUrl() === null;
}

/** The base every request is built on. */
export function apiBase(): string {
  const pinned = import.meta.env.VITE_API_BASE;
  if (pinned) return pinned;
  const server = getServerUrl();
  if (isNativeShell() && server) return `${server}${BROWSER_API_BASE}`;
  return BROWSER_API_BASE;
}
